import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Filter } from '~commonInfra/utils/Filter';
import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { IDataSourceBase } from '~src/datasource/models/IDataSourceInfo';
import { AddFavoriteRequest } from '~src/favorites/requests/AddFavoriteRequest';
import { ExploreFavoriteContentForUserRequest } from '~src/favorites/requests/ExploreFavoriteContentForUserRequest';
import { GetFavoritesRequest, IGetFavoritesResult } from '~src/favorites/requests/GetFavoritesRequest';
import { FavoriteOrdering, OrderFavoritesRequest } from '~src/favorites/requests/OrderFavoritesRequest';
import { RemoveFavoriteRequest } from '~src/favorites/requests/RemoveFavoriteRequest';
import { FavoriteItemType, IFavorite } from '~src/favorites/types/IFavorite';
import { FlowBaseInfo } from '~src/flow/models/FlowInfo';
import { WebClientListResourceFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ILensBase } from '~src/lens/models/ILensInfo';
import { isMetricsEnabled } from '~src/metrics/util/MetricUtils';
import {getSessionManagementServiceInstance} from '~src/sessionManagement/SessionManagementService';
import { ExploreContentApi_ContentType } from '~src/spacesPlatform/models/IContent';
import { ContentMapper } from '~src/spacesPlatform/models/mappers/ContentMapper';
import { IViewBase } from '~src/views/models/IViewInfo';
import { IWorkbookBase } from '~src/workbook/models/IWorkbookInfo';

import WebHttpClient from '../../httpClient/WebHttpClient';

class FavoritesResource {

  private getFavoritesFetcher = new WebClientListResourceFetcher(
    GetFavoritesRequest,
    (result: IGetFavoritesResult) => result.favorites);

  private exploreFavoriteContentForUserFetcher = new WebClientListResourceFetcher(
    ExploreFavoriteContentForUserRequest,
    ServerApiMapper.favoriteFromExploreFavoriteContentForUser);

  /**
   * Use this method if SOLR style metadata is needed or useCollectionsFavorites FF is off.
   * In general exploreFavoriteContentForUser (see method below) is more preferred.
   * @param contentTypes All supported types will be fetched unless contentTypes is specified.
   */
  public fetchFromGetFavorites(
    contentTypes?: ExploreContentApi_ContentType[],
  ): ResourceQuery<IFavorite> {
    const typesToFetch = (contentTypes ?? this.getSupportedContentTypes())
      .map(contentType => ContentMapper.ContentTypeToTargetMap[contentType]);
    const query = new ResourceQuery<IFavorite>(this.getFavoritesFetcher);

    return query.addFilter(Filter.stringFieldInArray('targetType', typesToFetch));
  }

  /**
   * This method calls the newer API exploreFavoriteContentForUser to fetch favorites.
   * @param contentTypes All supported types will be fetched unless contentTypes is specified.
   */
  public fetchFromExploreFavoriteContentForUser(
    contentTypes?: ExploreContentApi_ContentType[],
  ): ResourceQuery<IFavorite> {
    const typesToFetch = contentTypes ?? this.getSupportedContentTypes();
    const query = new ResourceQuery<IFavorite>(this.exploreFavoriteContentForUserFetcher);

    return query.addFilter(Filter.stringFieldInArray('contentType', typesToFetch));
  }

  private getSupportedContentTypes(): ExploreContentApi_ContentType[] {
    let supportedTypes: ExploreContentApi_ContentType[] = ['workbook', 'datasource', 'project', 'view', 'dataRole'];

    if (getSessionManagementServiceInstance().flowsOnSiteEnabled()) {
      supportedTypes.push('flow');
    }

    if (FeatureFlags.askDataLensFavoritingEnabled) {
      supportedTypes.push('lens');
    }

    if (isMetricsEnabled()) {
      supportedTypes.push('metric');
    }

    if (FeatureFlags.publishedConnectionContentManagementEnabled) {
      supportedTypes.push('virtualConnection');
      supportedTypes.push('virtualConnectionTable');
    }

    if (FeatureFlags.useCollectionsFavorites) {
      supportedTypes.push('collection');
    }

    return supportedTypes;
  }

  public addView(view: IViewBase): Promise<IResult> {
    return this.add(view.id, 'view');
  }

  public addWorkbook(workbook: IWorkbookBase): Promise<IResult> {
    return this.add(workbook.id, 'workbook');
  }

  public addDatasource(datasource: IDataSourceBase): Promise<IResult> {
    return this.add(datasource.idForFavoriting, 'datasource');
  }

  public addFlow(flow: FlowBaseInfo): Promise<IResult> {
    return this.add(flow.id, 'flow');
  }

  public addLens(lens: ILensBase): Promise<IResult> {
    return this.add(lens.id, 'lens');
  }

  public removeView(view: IViewBase): Promise<IResult> {
    return this.remove(view.id, 'view');
  }

  public removeViewById(id: string): Promise<IResult> {
    return this.remove(id, 'view');
  }

  public removeWorkbook(workbook: IWorkbookBase): Promise<IResult> {
    return this.remove(workbook.id, 'workbook');
  }

  public removeDatasource(datasource: IDataSourceBase): Promise<IResult> {
    return this.remove(datasource.idForFavoriting, 'datasource');
  }

  public removeFlow(flow: FlowBaseInfo): Promise<IResult> {
    return this.remove(flow.id, 'flow');
  }

  public removeLens(lens: ILensBase): Promise<IResult> {
    return this.remove(lens.id, 'lens');
  }

  public add(objectId: string, objectType: FavoriteItemType): Promise<IResult> {
    return WebHttpClient.sendRequest(new AddFavoriteRequest({
      objectId: objectId,
      objectType: objectType
    }));
  }

  public remove(objectId: string, objectType: FavoriteItemType): Promise<IResult> {
    return WebHttpClient.sendRequest(new RemoveFavoriteRequest({
      objectId: objectId,
      objectType: objectType
    }));
  }

  public orderFavorites(orderings: FavoriteOrdering[]) {
    return WebHttpClient.sendRequest(new OrderFavoritesRequest({ orderings }));
  }

}

export const Favorites = new FavoritesResource();
